import { useMemo, useState } from "react"
import { format } from "date-fns"
import { enUS, fr } from "date-fns/locale"
import { CalendarIcon, Factory, Info } from "lucide-react"
import { useTranslation } from "react-i18next"

import { StatusBadge } from "@/components/status-badge"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import type {
  MillFloorLoad,
  MillFloorQueueLot,
} from "@/features/mill-floor/api/types"
import {
  loadAvgBagKg,
  loadCooperativeName,
  loadRemainingBags,
  loadRemainingKg,
  qdlAvgBagKg,
  qdlRemainingBags,
  qdlRemainingKg,
} from "@/features/mill-floor/lib/load-math"
import { useScheduleUsinage } from "@/features/milling/hooks/use-milling-mutations"
import { LOT_TARGET_BAGS } from "@/features/milling/lib/quality"
import { formatNumber, kgToMt } from "@/lib/format"
import { cn } from "@/lib/utils"

interface ScheduleUsinageDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  availableLoads: MillFloorLoad[]
  availableQueueLots: MillFloorQueueLot[]
  onCreated: (usinageId: string | number) => void
}

function todayISO() {
  return new Date().toISOString().slice(0, 10)
}

function isoFromDate(d: Date) {
  return format(d, "yyyy-MM-dd")
}

// Selection state — two parallel maps, one per source stream. Each
// maps an id (ExporterLoads or ExporterLots row id) to the bag count
// the operator wants to reserve from it.
type Selection = Map<number, number>

export function ScheduleUsinageDialog({
  open,
  onOpenChange,
  availableLoads,
  availableQueueLots,
  onCreated,
}: ScheduleUsinageDialogProps) {
  const { t, i18n } = useTranslation("milling")
  const dateLocale = i18n.language?.startsWith("fr") ? fr : enUS
  const schedule = useScheduleUsinage()

  const [loadSel, setLoadSel] = useState<Selection>(() => new Map())
  const [qdlSel, setQdlSel] = useState<Selection>(() => new Map())
  const [operator, setOperator] = useState("")
  const [startDate, setStartDate] = useState<string>(todayISO())

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setLoadSel(new Map())
      setQdlSel(new Map())
      setOperator("")
      setStartDate(todayISO())
    }
    onOpenChange(next)
  }

  const toggleLoad = (load: MillFloorLoad) => {
    setLoadSel((prev) => {
      const next = new Map(prev)
      if (next.has(load.id)) next.delete(load.id)
      else next.set(load.id, loadRemainingBags(load))
      return next
    })
  }

  const setLoadBags = (load: MillFloorLoad, raw: number) => {
    const max = loadRemainingBags(load)
    const n = Math.max(0, Math.min(max, Math.floor(raw) || 0))
    setLoadSel((prev) => {
      const next = new Map(prev)
      next.set(load.id, n)
      return next
    })
  }

  const toggleQdl = (qdl: MillFloorQueueLot) => {
    setQdlSel((prev) => {
      const next = new Map(prev)
      if (next.has(qdl.id)) next.delete(qdl.id)
      else next.set(qdl.id, qdlRemainingBags(qdl))
      return next
    })
  }

  const setQdlBags = (qdl: MillFloorQueueLot, raw: number) => {
    const max = qdlRemainingBags(qdl)
    const n = Math.max(0, Math.min(max, Math.floor(raw) || 0))
    setQdlSel((prev) => {
      const next = new Map(prev)
      next.set(qdl.id, n)
      return next
    })
  }

  // Summary aggregates both selections so the operator sees one bags /
  // MT / lots-estimate strip regardless of which tab they're on.
  const summary = useMemo(() => {
    let bags = 0
    let kg = 0
    for (const [id, b] of loadSel.entries()) {
      const l = availableLoads.find((x) => x.id === id)
      if (!l) continue
      bags += b
      kg += b * loadAvgBagKg(l)
    }
    for (const [id, b] of qdlSel.entries()) {
      const q = availableQueueLots.find((x) => x.id === id)
      if (!q) continue
      bags += b
      kg += b * qdlAvgBagKg(q)
    }
    const fullLots = Math.floor(bags / LOT_TARGET_BAGS)
    const queueBags = bags - fullLots * LOT_TARGET_BAGS
    const partialLots = queueBags > 0 ? 1 : 0
    return {
      bags,
      mt: kgToMt(kg),
      count: loadSel.size + qdlSel.size,
      fullLots,
      queueBags,
      partialLots,
      totalLots: fullLots + partialLots,
    }
  }, [loadSel, qdlSel, availableLoads, availableQueueLots])

  const canCreate = summary.count > 0 && summary.bags > 0

  const handleCreate = () => {
    if (!canCreate) return
    const loadAssignments = Array.from(loadSel.entries())
      .filter(([, bags]) => bags > 0)
      .map(([loadId, bags]) => ({ loadId, bags }))
    const queueLotAssignments = Array.from(qdlSel.entries())
      .filter(([, bags]) => bags > 0)
      .map(([queueLotId, bags]) => ({ queueLotId, bags }))

    const trimmedOperator = operator.trim()
    schedule.mutate(
      {
        ...(trimmedOperator ? { operator: trimmedOperator } : {}),
        startDate,
        ...(loadAssignments.length > 0 ? { loadAssignments } : {}),
        ...(queueLotAssignments.length > 0 ? { queueLotAssignments } : {}),
      },
      { onSuccess: (result) => onCreated(result.id) }
    )
  }

  const startDateAsDate = useMemo(() => {
    const [y, m, d] = startDate.split("-").map(Number)
    if (!y || !m || !d) return new Date()
    return new Date(y, m - 1, d)
  }, [startDate])

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("schedule.title")}</DialogTitle>
          <DialogDescription>{t("schedule.subtitle")}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-5">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label
                htmlFor="usinage-operator"
                className="flex items-baseline gap-1.5 text-xs font-medium"
              >
                {t("schedule.operatorLabel")}
                <span className="text-muted-foreground text-[10px] font-normal lowercase">
                  {t("schedule.optional")}
                </span>
              </Label>
              <Input
                id="usinage-operator"
                value={operator}
                placeholder={t("schedule.operatorPlaceholder")}
                onChange={(e) => setOperator(e.target.value)}
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">
                {t("schedule.startDateLabel")}
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start font-normal",
                      !startDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="size-4" />
                    {startDate
                      ? format(startDateAsDate, "PPP", { locale: dateLocale })
                      : t("schedule.startDatePlaceholder")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={startDateAsDate}
                    onSelect={(d) => {
                      if (d) setStartDate(isoFromDate(d))
                    }}
                    locale={dateLocale}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
              <Label className="text-xs font-medium">
                {t("schedule.loadsLabel")}
              </Label>
              {summary.count > 0 ? (
                <div className="flex items-center gap-2 text-sm tabular-nums">
                  <KpiTooltip label={t("schedule.kpiBagsTooltip")}>
                    <span className="font-semibold">
                      {formatNumber(summary.bags)}
                      <span className="ml-1 text-xs text-muted-foreground">
                        {t("schedule.bagsShort")}
                      </span>
                    </span>
                  </KpiTooltip>
                  <span className="text-muted-foreground">·</span>
                  <KpiTooltip label={t("schedule.kpiMtTooltip")}>
                    <span className="font-semibold">
                      {formatNumber(summary.mt, 2)}
                      <span className="ml-1 text-xs text-muted-foreground">
                        MT
                      </span>
                    </span>
                  </KpiTooltip>
                  <span className="text-muted-foreground">→</span>
                  <LotsChips
                    fullLots={summary.fullLots}
                    partialLots={summary.partialLots}
                    queueBags={summary.queueBags}
                  />
                </div>
              ) : (
                <span className="text-xs text-muted-foreground tabular-nums">
                  {t("schedule.summaryEmpty", {
                    total: availableLoads.length + availableQueueLots.length,
                  })}
                </span>
              )}
            </div>

            <Tabs defaultValue="loads" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="loads">
                  {t("schedule.tabs.loads", {
                    count: availableLoads.length,
                  })}
                </TabsTrigger>
                <TabsTrigger value="qdls">
                  {t("schedule.tabs.queueLots", {
                    count: availableQueueLots.length,
                  })}
                </TabsTrigger>
              </TabsList>
              <TabsContent value="loads" className="mt-2">
                {availableLoads.length === 0 ? (
                  <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
                    {t("schedule.empty")}
                  </div>
                ) : (
                  <div className="max-h-80 overflow-y-auto rounded-md border">
                    {availableLoads.map((load, i) => {
                      const assigned = loadSel.get(load.id) ?? 0
                      const checked = loadSel.has(load.id)
                      return (
                        <LoadPickerRow
                          key={load.id}
                          load={load}
                          checked={checked}
                          assigned={assigned}
                          onToggle={() => toggleLoad(load)}
                          onSetBags={(n) => setLoadBags(load, n)}
                          isLast={i === availableLoads.length - 1}
                        />
                      )
                    })}
                  </div>
                )}
              </TabsContent>
              <TabsContent value="qdls" className="mt-2">
                {availableQueueLots.length === 0 ? (
                  <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
                    {t("schedule.emptyQueueLots")}
                  </div>
                ) : (
                  <div className="max-h-80 overflow-y-auto rounded-md border">
                    {availableQueueLots.map((qdl, i) => {
                      const assigned = qdlSel.get(qdl.id) ?? 0
                      const checked = qdlSel.has(qdl.id)
                      return (
                        <QueueLotPickerRow
                          key={qdl.id}
                          qdl={qdl}
                          checked={checked}
                          assigned={assigned}
                          onToggle={() => toggleQdl(qdl)}
                          onSetBags={(n) => setQdlBags(qdl, n)}
                          isLast={i === availableQueueLots.length - 1}
                        />
                      )
                    })}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={schedule.isPending}
          >
            {t("schedule.cancel")}
          </Button>
          <Button
            onClick={handleCreate}
            disabled={!canCreate || schedule.isPending}
          >
            <Factory className="size-4" />
            {summary.count > 0
              ? t("schedule.createWithCount", {
                  count: summary.count,
                  mt: formatNumber(summary.mt, 2),
                })
              : t("schedule.create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function LoadPickerRow({
  load,
  checked,
  assigned,
  onToggle,
  onSetBags,
  isLast,
}: {
  load: MillFloorLoad
  checked: boolean
  assigned: number
  onToggle: () => void
  onSetBags: (n: number) => void
  isLast: boolean
}) {
  const { t } = useTranslation("milling")
  const remaining = loadRemainingBags(load)
  const mt = kgToMt(loadRemainingKg(load))
  const bol = load.DeliveryForms?.billOfLadingNumber ?? null
  const identity = bol ?? `#${load.loadNumber}`
  const identityTooltip = bol
    ? t("schedule.cccTooltip")
    : t("schedule.loadNumberTooltip")

  return (
    <div
      className={cn(
        "flex items-center gap-3 px-4 py-3 transition-colors",
        checked ? "bg-primary/5" : "hover:bg-muted/40",
        isLast ? "" : "border-b",
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-pressed={checked}
        className="flex min-w-0 flex-1 items-center gap-3 text-left"
      >
        <Checkbox className="border-foreground" checked={checked} />

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="font-mono text-sm font-medium decoration-muted-foreground/50 decoration-dotted underline-offset-4">
                  {identity}
                </span>
              </TooltipTrigger>
              <TooltipContent>{identityTooltip}</TooltipContent>
            </Tooltip>
            {load.grade ? (
              <StatusBadge
                tone={load.grade === "GRADE_2" ? "warning" : "success"}
              >
                {load.grade.replace("_", " ")}
              </StatusBadge>
            ) : null}
          </div>
          <div className="mt-0.5 truncate text-xs text-muted-foreground">
            {loadCooperativeName(load) ?? "—"}
          </div>
        </div>
      </button>

      {checked ? (
        <BagsAssignedInput
          assigned={assigned}
          remaining={remaining}
          onSetBags={onSetBags}
        />
      ) : (
        <BagsRemainingPreview mt={mt} remaining={remaining} />
      )}
    </div>
  )
}

// Queue de Lot picker row — same shape as LoadPickerRow but reads QDL
// fields. The "Queue de Lot" badge replaces the grade chip; identity
// is the lot number; the subtitle reminds the operator the row comes
// from a previous usinage's leftover.
function QueueLotPickerRow({
  qdl,
  checked,
  assigned,
  onToggle,
  onSetBags,
  isLast,
}: {
  qdl: MillFloorQueueLot
  checked: boolean
  assigned: number
  onToggle: () => void
  onSetBags: (n: number) => void
  isLast: boolean
}) {
  const { t } = useTranslation("milling")
  const remaining = qdlRemainingBags(qdl)
  const mt = kgToMt(qdlRemainingKg(qdl))

  return (
    <div
      className={cn(
        "flex items-center gap-3 px-4 py-3 transition-colors",
        checked ? "bg-primary/5" : "hover:bg-muted/40",
        isLast ? "" : "border-b",
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-pressed={checked}
        className="flex min-w-0 flex-1 items-center gap-3 text-left"
      >
        <Checkbox className="border-foreground" checked={checked} />

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="font-mono text-sm font-medium decoration-muted-foreground/50 decoration-dotted underline-offset-4">
                  {qdl.lotNumber ?? `#${qdl.id}`}
                </span>
              </TooltipTrigger>
              <TooltipContent>{t("schedule.qdlIdTooltip")}</TooltipContent>
            </Tooltip>
            <StatusBadge tone="warning">{t("schedule.qdlBadge")}</StatusBadge>
          </div>
          <div className="mt-0.5 truncate text-xs text-muted-foreground">
            {t("schedule.qdlSubtitle")}
          </div>
        </div>
      </button>

      {checked ? (
        <BagsAssignedInput
          assigned={assigned}
          remaining={remaining}
          onSetBags={onSetBags}
        />
      ) : (
        <BagsRemainingPreview mt={mt} remaining={remaining} />
      )}
    </div>
  )
}

function BagsAssignedInput({
  assigned,
  remaining,
  onSetBags,
}: {
  assigned: number
  remaining: number
  onSetBags: (n: number) => void
}) {
  const { t } = useTranslation("milling")
  return (
    <div className="flex flex-shrink-0 items-center gap-1.5">
      <Input
        type="number"
        inputMode="numeric"
        min={0}
        max={remaining}
        value={assigned > 0 ? assigned : ""}
        onChange={(e) => {
          const raw = e.target.value
          onSetBags(raw === "" ? 0 : Number(raw))
        }}
        onFocus={(e) => e.target.select()}
        placeholder="0"
        aria-invalid={assigned <= 0}
        className={cn(
          "h-8 w-20 text-right text-sm tabular-nums",
          assigned <= 0 &&
            "border-destructive focus-visible:ring-destructive/30",
        )}
      />
      <span className="text-xs text-muted-foreground tabular-nums">
        / {formatNumber(remaining)} {t("schedule.bagsShort")}
      </span>
    </div>
  )
}

function BagsRemainingPreview({
  mt,
  remaining,
}: {
  mt: number
  remaining: number
}) {
  const { t } = useTranslation("milling")
  return (
    <div className="flex-shrink-0 text-right text-xs tabular-nums">
      <div className="font-medium">{formatNumber(mt, 2)} MT</div>
      <div className="text-muted-foreground">
        {formatNumber(remaining)} {t("schedule.bagsShort")}
      </div>
    </div>
  )
}

function LotsChips({
  fullLots,
  partialLots,
  queueBags,
}: {
  fullLots: number
  partialLots: number
  queueBags: number
}) {
  const { t } = useTranslation("milling")
  if (fullLots === 0 && partialLots === 0) return null
  return (
    <span className="inline-flex items-center gap-1">
      {fullLots > 0 ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex cursor-help items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-semibold text-emerald-700 tabular-nums dark:text-emerald-300">
              {fullLots} {t("schedule.fullLotsHint")}
              <Info className="size-3 opacity-60" />
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            {t("schedule.fullLotsTooltip", {
              count: fullLots,
              target: LOT_TARGET_BAGS,
            })}
          </TooltipContent>
        </Tooltip>
      ) : null}
      {partialLots > 0 ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex cursor-help items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-xs font-semibold text-amber-700 tabular-nums dark:text-amber-300">
              {fullLots > 0 ? "+ " : ""}
              {partialLots} {t("schedule.partialLotHint")}
              <Info className="size-3 opacity-60" />
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            {t("schedule.partialLotTooltip", {
              queue: queueBags,
              target: LOT_TARGET_BAGS,
            })}
          </TooltipContent>
        </Tooltip>
      ) : null}
    </span>
  )
}

function KpiTooltip({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex cursor-help items-center gap-1">
          {children}
          <Info className="size-3 text-muted-foreground opacity-70" />
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{label}</TooltipContent>
    </Tooltip>
  )
}
